// src/pages/Home.js
import React, { useState } from 'react';
import heroImg from '../assets/gym-hero.jpg';

const heroBgStyle = {
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(${heroImg})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

export default function Home() {
  const gymOnlyBilling = [
    { label: '1 Month', price: '60 EUR' },
    { label: '3 Months', price: '165 EUR' },
    { label: '6 Months', price: '300 EUR' },
    { label: '1 Year', price: '540 EUR' },
  ];

  const gymOnlyOptions = [
    'Unlimited gym floor access',
    'Free weights and machines',
    'Independent training support',
  ];

  const classesOnlyOptions = [
    'Access to all group classes',
    'Coach-led sessions',
    'Structured weekly training schedule',
  ];

  const comboOptions = [
    'Unlimited gym access',
    'Glutes & Abs',
    'Circuit Functional',
    'Box Fit',
    'Conditioning',
  ];

  const classes = [
    {
      title: 'Spin Bikes',
      desc: 'High-energy cycle sessions for endurance and calorie burn.',
      img: '/images/class-spin-bikes.webp',
    },
    {
      title: 'Abs & Glutes',
      desc: 'Targeted classes focused on core strength and lower-body development.',
      img: '/images/class-abs-glutes.webp',
    },
    {
      title: 'Box Fit',
      desc: 'Boxing-inspired conditioning with technique, speed, and power work.',
      img: '/images/class-box-fit.webp',
    },
    {
      title: 'Circuit Training',
      desc: 'Full-body stations that build strength, stamina, and athletic performance.',
      img: '/images/class-circuit-training.webp',
    },
  ];

  const facilities = [
    {
      key: 'monsters',
      title: 'Home of Monsters',
      img: '/images/tile-monsters.webp',
      desc: 'Where heavy PRs are built daily. This is our flagship strength zone for serious lifters chasing elite performance.',
    },
    {
      key: 'green',
      title: 'Green room',
      img: '/images/tile-green.webp',
      desc: 'Your recovery and reset area. Mobility flows, breath work, and focused warm-ups happen here before the grind.',
    },
    {
      key: 'red',
      title: 'Read room',
      img: '/images/tile-red.webp',
      desc: 'A high-focus coaching corner for form analysis, training strategy, and progression planning with your coach.',
    },
    {
      key: 'cardio',
      title: 'Cardio',
      img: '/images/tile-cardio.webp',
      desc: 'Engine work with purpose. Intervals, endurance blocks, and fat-burning sessions designed to boost total conditioning.',
    },
  ];

  const storyParagraphs = [
    'Founded in 2016 as a boutique fitness studio in Nicosia, Quantum Gym evolved into a dedicated bodybuilding and strength center focused on measurable progress.',
    'Today we are the premier powerlifting training center in Cyprus—known for top-of-the-line equipment, expert coaching, and a supportive community.',
  ];

  const [activeFacility, setActiveFacility] = useState('monsters');

  return (
    <main>
      {/* Hero */}
      <section
        id="home"
        className="onepage-section justify-content-center text-center text-white px-3"
        style={heroBgStyle}
      >
        <div className="container py-5" />
      </section>

      {/* About */}
      <section id="about" className="onepage-section bg-body-secondary">
        <div className="container">
          <h2 className="text-center mb-4 mb-md-5">About Us</h2>
          <div className="row justify-content-center">
            <div className="col-12 col-lg-9">
              <div className="text-center mx-auto" style={{ maxWidth: '48rem' }}>
                {storyParagraphs.map((text, i) => (
                  <p key={i} className={i === storyParagraphs.length - 1 ? 'mb-0' : 'mb-3'}>
                    {text}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we offer */}
      <section id="memberships" className="onepage-section">
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-stretch section5-membership-grid">
            <div className="col-12 col-xl-6">
              <div className="membership-hero-card h-100 p-4 p-md-5 p-xl-6">
                <p className="membership-kicker mb-2">Most Flexible</p>
                <h3 className="display-6 fw-bold mb-2">Gym Only</h3>
                <p className="lead mb-4 text-white-50">Unlimited access to the gym floor and equipment.</p>

                <div className="row g-3 mb-4">
                  {gymOnlyBilling.map((item) => (
                    <div key={item.label} className="col-6">
                      <div className="billing-option-box h-100">
                        <p className="small text-uppercase mb-1 text-white-50">{item.label}</p>
                        <p className="h5 fw-bold mb-0">{item.price}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <ul className="mb-0 text-white-50">
                  {gymOnlyOptions.map((opt) => (
                    <li key={opt}>{opt}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="rounded-3 border shadow-sm p-4 p-md-5 p-xl-6 h-100 membership-plan-card membership-plan-card--classes">
                <p className="membership-card-kicker mb-2">Coach Led</p>
                <h3 className="h3 fw-bold mb-2">Classes Only</h3>
                <p className="membership-price mb-4">75 EUR / month</p>
                <p className="small text-uppercase text-body-secondary mb-2 fw-semibold">Options</p>
                <ul className="mb-0 membership-options-list">
                  {classesOnlyOptions.map((opt) => (
                    <li key={opt}>{opt}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="rounded-3 border shadow-sm p-4 p-md-5 p-xl-6 h-100 membership-plan-card membership-plan-card--combo">
                <p className="membership-card-kicker mb-2">Best Seller</p>
                <h3 className="h3 fw-bold mb-2">Gym &amp; Classes Combo</h3>
                <p className="membership-price mb-4">95 EUR / month</p>
                <p className="small text-uppercase text-body-secondary mb-2 fw-semibold">Options</p>
                <ul className="mb-0 membership-options-list">
                  {comboOptions.map((opt) => (
                    <li key={opt}>{opt}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Classes */}
      <section id="classes" className="onepage-section bg-body-secondary classes-fullscreen-section">
        <div className="container">
          <div
            id="classesCarousel"
            className="carousel slide classes-carousel"
            data-bs-ride="carousel"
            data-bs-interval="3000"
            data-bs-pause="hover"
          >
            <div className="carousel-indicators">
              {classes.map((c, idx) => (
                <button
                  key={`indicator-${c.title}`}
                  type="button"
                  data-bs-target="#classesCarousel"
                  data-bs-slide-to={idx}
                  className={idx === 0 ? 'active' : ''}
                  aria-current={idx === 0 ? 'true' : undefined}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>

            <div className="carousel-inner overflow-hidden">
              {classes.map((c, idx) => (
                <div key={c.title} className={`carousel-item ${idx === 0 ? 'active' : ''}`}>
                  <div className="classes-carousel-slide">
                    <img src={c.img} alt={c.title} className="classes-carousel-image" loading="lazy" />
                    <div className="classes-carousel-overlay">
                      <h3 className="h2 fw-bold mb-2">{c.title}</h3>
                      <p className="mb-0 text-white-50">{c.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="carousel-control-prev"
              type="button"
              data-bs-target="#classesCarousel"
              data-bs-slide="prev"
            >
              <span className="carousel-control-prev-icon" aria-hidden="true" />
              <span className="visually-hidden">Previous</span>
            </button>
            <button
              className="carousel-control-next"
              type="button"
              data-bs-target="#classesCarousel"
              data-bs-slide="next"
            >
              <span className="carousel-control-next-icon" aria-hidden="true" />
              <span className="visually-hidden">Next</span>
            </button>
          </div>
        </div>
      </section>

      {/* Facilities tiles */}
      <section id="facilities" className="facility-tiles-fullscreen" aria-label="Facilities">
        <div className="facility-tiles-grid">
          {facilities.map((facility) => {
            const isActive = activeFacility === facility.key;
            return (
              <article
                key={facility.key}
                className={`facility-tile ${isActive ? 'is-active' : ''}`}
                tabIndex={0}
                role="button"
                aria-pressed={isActive}
                onClick={() => setActiveFacility(facility.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveFacility(facility.key);
                  }
                }}
              >
                <img src={facility.img} alt={facility.title} className="facility-tile-image" />
                <div className="facility-tile-overlay">
                  <h3 className="facility-tile-title">{facility.title}</h3>
                  <p className="facility-tile-desc">{facility.desc}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

    </main>
  );
}
